import type { DocsService } from '@affine/core/modules/doc';
import type { TagService } from '@affine/core/modules/tag';
import type { WorkbenchService } from '@affine/core/modules/workbench';
import type { AffineTextAttributes } from '@blocksuite/affine/shared/types';
import { type DeltaInsert, Text } from '@blocksuite/affine/store';

import type { NotesIndexEntry } from './add-to-notes';
import { updateNotesEntry } from './add-to-notes';
import { notesHref } from './notes-link-format';
import { sourceHref } from './source-link-format';

const PERMANENT_TAG_LABEL = 'Permanent Note';
const PERMANENT_TAG_COLOR = '#7C3AED'; // violet — distinct from Notes amber / Branch teal
const TITLE_WORD_LIMIT = 8;
const TITLE_CHAR_LIMIT = 80;

/**
 * Derive a tentative title from the clipped quote — the first ~8
 * words (capped at 80 chars), with trailing punctuation stripped.
 * The researcher is expected to rewrite this on first edit; we just
 * want the sidebar entry to be readable instead of "Untitled".
 */
function deriveTitle(passage: string): string {
  const oneLine = passage.replace(/\s+/g, ' ').trim();
  if (!oneLine) return 'Permanent note';
  const words = oneLine.split(' ').slice(0, TITLE_WORD_LIMIT).join(' ');
  const trimmed =
    words.length > TITLE_CHAR_LIMIT
      ? `${words.slice(0, TITLE_CHAR_LIMIT).trimEnd()}\u2026`
      : words;
  return trimmed.replace(/[.,;:!?]+$/, '');
}

function ensurePermanentTag(
  tagService: TagService
): { tag: (docId: string) => void } | null {
  try {
    const existing = tagService.tagList.tags$.value.find(
      t => t.value$.value === PERMANENT_TAG_LABEL
    );
    if (existing) {
      return { tag: (id: string) => existing.tag(id) };
    }
    const created = tagService.tagList.createTag(
      PERMANENT_TAG_LABEL,
      PERMANENT_TAG_COLOR
    );
    return { tag: (id: string) => created.tag(id) };
  } catch (err) {
    console.warn('[promote-to-permanent-note] tag creation failed', err);
    return null;
  }
}

type OpenedDoc = {
  doc: {
    waitForSyncReady: () => Promise<void>;
    addPriorityLoad: (n: number) => () => void;
    blockSuiteDoc: {
      getBlocksByFlavour: (
        flavour: string
      ) => Array<{ id: string; model: unknown }>;
      addBlock: (
        flavour: string,
        props: Record<string, unknown>,
        parentId?: string,
        index?: number
      ) => string;
      transact: (fn: () => void) => void;
      readonly?: boolean;
    };
  };
  release: () => void;
};

async function openDoc(
  docsService: DocsService,
  docId: string
): Promise<OpenedDoc> {
  return (
    docsService as unknown as { open: (id: string) => OpenedDoc }
  ).open(docId);
}

export interface PromoteToPermanentNoteOptions {
  docsService: DocsService;
  tagService: TagService;
  workbench: WorkbenchService;
  notesDocId: string;
  entry: NotesIndexEntry;
  /** Best-effort source-doc title for the attribution line. */
  sourceTitle?: string;
}

export interface PromoteToPermanentNoteResult {
  permanentDocId: string;
  title: string;
}

export interface CreatePermanentNoteFromSynthesisOptions {
  docsService: DocsService;
  tagService: TagService;
  workbench: WorkbenchService;
  notesDocId: string;
  /** Markdown produced by the synthesis call. */
  synthesisText: string;
  /** Source entries the synthesis was built from. */
  entries: ReadonlyArray<NotesIndexEntry>;
}

interface SourceTitleResolver {
  (docId: string): string;
}

/**
 * Promote a fleeting clipping into a standalone atomic Permanent
 * Note — the Zettelkasten step that researchers consistently call
 * out as the one that makes notes apps actually useful for thesis
 * writing.
 *
 * Body shape (newest BlockSuite convention, all four blocks belong
 * to the doc's main affine:note):
 *
 *   1. H1 heading — empty, placeholder for the researcher's atomic
 *      insight ("This is what I think this quote MEANS").
 *   2. Blockquote — the original passage, frozen.
 *   3. Source-attribution paragraph — "From: <Source title ↗>" with
 *      an inline curiescious-source:// link routed through
 *      CanvasLinkRouter to scroll the original passage into view.
 *   4. Empty paragraph — cursor lands here.
 *   5. Footer paragraph — "Promoted from a fleeting note" with an
 *      inline curiescious-notes:// link back to the divider in the
 *      accumulator doc, so the researcher can always retrace where
 *      they came from.
 *
 * After the body is written, the fleeting entry is patched:
 *   status: 'permanent', promotedToDocId: <new doc id>
 *
 * The Browse-page row then renders an "↗ Permanent note" link and
 * the stats pill counts it under "permanent."
 */
export async function promoteToPermanentNote(
  opts: PromoteToPermanentNoteOptions
): Promise<PromoteToPermanentNoteResult> {
  const { docsService, tagService, workbench, notesDocId, entry, sourceTitle } =
    opts;

  const title = deriveTitle(entry.sourcePassage);
  const passage = entry.sourcePassage.replace(/\s+/g, ' ').trim();
  const sourceLabel = sourceTitle?.trim() || 'source';
  const sourceLink =
    entry.sourceDocId && entry.sourceBlockId
      ? sourceHref(entry.sourceDocId, entry.sourceBlockId)
      : undefined;
  const fleetingLink = notesHref(notesDocId, entry.entryId);

  // 1. Create the doc + set the title before opening so the sidebar
  // entry reads sensibly the moment the workbench mounts the new
  // pane.
  const record = docsService.createDoc({ primaryMode: 'page' });
  const permanentDocId = record.id;
  try {
    await docsService.changeDocTitle(permanentDocId, title);
  } catch (err) {
    console.warn('[promote-to-permanent-note] title set failed', err);
  }

  // 2. Tag with `Permanent Note` so the sidebar entry + a future
  // `?status=permanent` filter both work via the same lookup.
  const tag = ensurePermanentTag(tagService);
  if (tag) {
    try {
      tag.tag(permanentDocId);
    } catch (err) {
      console.warn('[promote-to-permanent-note] tag apply failed', err);
    }
  }

  // 3. Seed the body. Open the doc, wait for sync, write blocks in
  // a single transaction, release.
  const opened = await openDoc(docsService, permanentDocId);
  const dispose = opened.doc.addPriorityLoad(10);
  try {
    await opened.doc.waitForSyncReady();
    dispose();
    const bsDoc = opened.doc.blockSuiteDoc;
    const wasReadonly = bsDoc.readonly === true;
    if (wasReadonly) bsDoc.readonly = false;
    try {
      const notes = bsDoc.getBlocksByFlavour('affine:note');
      const noteId = notes[0]?.id;
      if (!noteId) {
        console.warn(
          '[promote-to-permanent-note] doc has no affine:note block'
        );
      } else {
        bsDoc.transact(() => {
          // 1. H1 heading — placeholder for the atomic insight.
          bsDoc.addBlock(
            'affine:paragraph',
            { type: 'h1', text: new Text(title) },
            noteId
          );
          // 2. Quote block.
          bsDoc.addBlock(
            'affine:paragraph',
            { type: 'quote', text: new Text(passage) },
            noteId
          );
          // 3. Source-attribution paragraph with inline source link.
          const attribDelta: DeltaInsert<AffineTextAttributes>[] = sourceLink
            ? [
                { insert: 'From: ' },
                {
                  insert: `${sourceLabel} \u2197`,
                  attributes: { link: sourceLink },
                },
              ]
            : [{ insert: `From: ${sourceLabel}` }];
          bsDoc.addBlock(
            'affine:paragraph',
            {
              type: 'text',
              text: new Text(attribDelta),
            },
            noteId
          );
          // 4. Empty paragraph — cursor target for the researcher.
          bsDoc.addBlock('affine:paragraph', {}, noteId);
          // 5. Footer back-link to the fleeting note.
          const fleetingDelta: DeltaInsert<AffineTextAttributes>[] = [
            { insert: 'Promoted from a ' },
            {
              insert: 'fleeting note',
              attributes: { link: fleetingLink },
            },
          ];
          bsDoc.addBlock(
            'affine:paragraph',
            { type: 'text', text: new Text(fleetingDelta) },
            noteId
          );
        });
      }
    } finally {
      if (wasReadonly) bsDoc.readonly = true;
    }
  } catch (err) {
    console.warn('[promote-to-permanent-note] body seed failed', err);
  } finally {
    opened.release();
  }

  // 4. Patch the fleeting entry — flip status + record the new
  // doc id so the Browse-page row can render the "↗ Permanent
  // note" link and the stats pill counts it as permanent.
  updateNotesEntry(docsService, notesDocId, entry.entryId, {
    status: 'permanent',
    promotedToDocId: permanentDocId,
  });

  // 5. Open the permanent note in the active view so the
  // researcher lands in it ready to write the atomic insight.
  try {
    workbench.workbench.openDoc(permanentDocId);
  } catch (err) {
    console.warn('[promote-to-permanent-note] openDoc failed', err);
  }

  return { permanentDocId, title };
}

function deriveSynthesisTitle(synthesisText: string): string {
  // Prefer the first H1 / H2 if the model emitted one — researchers
  // can see at a glance what the synthesis is about.
  const headingMatch = synthesisText.match(/^\s*#{1,3}\s+(.+)$/m);
  if (headingMatch?.[1]) {
    const heading = headingMatch[1].trim();
    return heading.length > TITLE_CHAR_LIMIT
      ? `${heading.slice(0, TITLE_CHAR_LIMIT).trimEnd()}\u2026`
      : heading;
  }
  // Fall back to first sentence / first ~8 words.
  const oneLine = synthesisText.replace(/[#*_>`]/g, '').replace(/\s+/g, ' ').trim();
  if (!oneLine) return 'Synthesis';
  const words = oneLine.split(' ').slice(0, TITLE_WORD_LIMIT).join(' ');
  const trimmed =
    words.length > TITLE_CHAR_LIMIT
      ? `${words.slice(0, TITLE_CHAR_LIMIT).trimEnd()}\u2026`
      : words;
  return trimmed.replace(/[.,;:!?]+$/, '') || 'Synthesis';
}

/**
 * Create a Permanent Note from a multi-source synthesis. Body shape:
 *
 *   1. H1 — derived from the synthesis (first heading or first ~8 words).
 *   2. H2 "Synthesis" — the AI-produced text rendered as paragraphs
 *      (line-broken by blank lines). Inline [Qn] markers are kept
 *      as-is; future work can resolve them to actual entry links.
 *   3. H2 "Sources" — for each fleeting entry: blockquote + inline
 *      link to the source block AND a back-link to the fleeting note
 *      via curiescious-notes://.
 *
 * After the body is written, each fleeting entry is patched with
 * `status: 'permanent'` and `promotedToDocId: <new doc id>` so the
 * Browse-page row shows the "↗ Permanent note" link.
 */
export async function createPermanentNoteFromSynthesis(
  opts: CreatePermanentNoteFromSynthesisOptions
): Promise<PromoteToPermanentNoteResult> {
  const { docsService, tagService, workbench, notesDocId, synthesisText, entries } =
    opts;

  const title = deriveSynthesisTitle(synthesisText);
  const resolveSourceTitle: SourceTitleResolver = docId => {
    try {
      const record = docsService.list.doc$(docId).value;
      return record?.meta$.value.title?.trim() || 'source';
    } catch {
      return 'source';
    }
  };

  // 1. Create + title the doc.
  const record = docsService.createDoc({ primaryMode: 'page' });
  const permanentDocId = record.id;
  try {
    await docsService.changeDocTitle(permanentDocId, title);
  } catch (err) {
    console.warn('[create-permanent-from-synthesis] title set failed', err);
  }

  // 2. Apply Permanent Note tag.
  const tag = ensurePermanentTag(tagService);
  if (tag) {
    try {
      tag.tag(permanentDocId);
    } catch (err) {
      console.warn('[create-permanent-from-synthesis] tag apply failed', err);
    }
  }

  // 3. Seed body.
  const opened = await openDoc(docsService, permanentDocId);
  const dispose = opened.doc.addPriorityLoad(10);
  try {
    await opened.doc.waitForSyncReady();
    dispose();
    const bsDoc = opened.doc.blockSuiteDoc;
    const wasReadonly = bsDoc.readonly === true;
    if (wasReadonly) bsDoc.readonly = false;
    try {
      const notes = bsDoc.getBlocksByFlavour('affine:note');
      const noteId = notes[0]?.id;
      if (noteId) {
        bsDoc.transact(() => {
          // H1 title
          bsDoc.addBlock(
            'affine:paragraph',
            { type: 'h1', text: new Text(title) },
            noteId
          );
          // H2 Synthesis section
          bsDoc.addBlock(
            'affine:paragraph',
            { type: 'h2', text: new Text('Synthesis') },
            noteId
          );
          // Split synthesis on blank lines so paragraphs render as
          // separate blocks. Headings within the synthesis stay as
          // plain text for v1; future work can parse markdown more
          // faithfully.
          const paragraphs = synthesisText
            .replace(/\r\n?/g, '\n')
            .split(/\n{2,}/)
            .map(p => p.trim())
            .filter(Boolean);
          for (const para of paragraphs) {
            bsDoc.addBlock(
              'affine:paragraph',
              { type: 'text', text: new Text(para) },
              noteId
            );
          }
          // H2 Sources section
          bsDoc.addBlock(
            'affine:paragraph',
            { type: 'h2', text: new Text('Sources') },
            noteId
          );
          // Per-entry: blockquote + attribution paragraph with source
          // link and back-link to the fleeting entry.
          entries.forEach((entry, idx) => {
            const marker = `[Q${idx + 1}]`;
            const passage = entry.sourcePassage.replace(/\s+/g, ' ').trim();
            bsDoc.addBlock(
              'affine:paragraph',
              { type: 'quote', text: new Text(`${marker} ${passage}`) },
              noteId
            );
            const sourceLabel = entry.sourceDocId
              ? resolveSourceTitle(entry.sourceDocId)
              : 'source';
            const sourceLink =
              entry.sourceDocId && entry.sourceBlockId
                ? sourceHref(entry.sourceDocId, entry.sourceBlockId)
                : undefined;
            const fleetingLink = notesHref(notesDocId, entry.entryId);
            const delta: DeltaInsert<AffineTextAttributes>[] = sourceLink
              ? [
                  { insert: 'From: ' },
                  {
                    insert: `${sourceLabel} \u2197`,
                    attributes: { link: sourceLink },
                  },
                  { insert: ' · ' },
                  {
                    insert: 'fleeting note',
                    attributes: { link: fleetingLink },
                  },
                ]
              : [
                  { insert: 'From: ' },
                  {
                    insert: 'fleeting note',
                    attributes: { link: fleetingLink },
                  },
                ];
            bsDoc.addBlock(
              'affine:paragraph',
              { type: 'text', text: new Text(delta) },
              noteId
            );
          });
        });
      }
    } finally {
      if (wasReadonly) bsDoc.readonly = true;
    }
  } catch (err) {
    console.warn('[create-permanent-from-synthesis] body seed failed', err);
  } finally {
    opened.release();
  }

  // 4. Patch every fleeting entry: status=permanent + promotedToDocId.
  for (const entry of entries) {
    updateNotesEntry(docsService, notesDocId, entry.entryId, {
      status: 'permanent',
      promotedToDocId: permanentDocId,
    });
  }

  // 5. Open the new permanent note.
  try {
    workbench.workbench.openDoc(permanentDocId);
  } catch (err) {
    console.warn('[create-permanent-from-synthesis] openDoc failed', err);
  }

  return { permanentDocId, title };
}
